/**
 * Dark mode for the form engine, without a dark mode in the form engine.
 *
 * The form components are Chakra with palette tokens written straight into
 * them — `color='gray.600'`, `bg='white'`, `primary.500` — several hundred of
 * them. Chakra compiles every one to a CSS variable
 * (`var(--chakra-colors-gray-600)`), and its own component themes reference
 * the same tokens. So instead of teaching every component a second palette,
 * this redefines those variables once, under one attribute on <html>:
 *
 *     html[data-crosure-color-mode="dark"] { --chakra-colors-gray-50: …; … }
 *
 * Chakra itself stays in LIGHT mode (nothing persisted, nothing for a
 * dashboard to inherit) and the whole palette is mirrored — 50↔900, 100↔800,
 * … 400↔500 — so a light shade used as a surface becomes a dark one and a
 * dark shade used as text becomes a light one. With the attribute absent the
 * block matches nothing: light mode is byte-identical to before.
 *
 * Neutrals go through `--crosure-dark-*` knobs, falling back to the mirrored
 * value, so a tenant can match its own dark palette from CSS it already owns
 * (its root's stylesheet, or the Studio design system's custom CSS).
 *
 * Who sets the attribute: the tenant root, when rendered with
 * `surface="form"` — it owns the theme toggle and the visitor's choice. See
 * components/page/TenantPageRoot.tsx (TenantShell), which also removes it
 * when the form unmounts.
 */

export const COLOR_MODE_ATTRIBUTE = 'data-crosure-color-mode'

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const

/** Tenant-tunable neutrals: Chakra variable → knob. */
const GRAY_KNOBS: Record<number, string> = {
	50: '--crosure-dark-surface',
	100: '--crosure-dark-surface-muted',
	200: '--crosure-dark-border',
	300: '--crosure-dark-border-strong',
	500: '--crosure-dark-fg-muted',
	600: '--crosure-dark-fg-muted',
	700: '--crosure-dark-fg',
	800: '--crosure-dark-fg',
	900: '--crosure-dark-fg',
}

type Palette = Record<string | number, unknown>

/** Colour syntax only. Brand palettes derive from Studio-configured values,
 *  and this string lands in a <style> element, so anything that is not
 *  plainly a colour is refused rather than escaped. */
const COLOR_VALUE = /^(#[0-9a-f]{3,8}|(rgba?|hsla?)\([\d\s.,%/-]+\))$/i

const isPalette = (value: unknown): value is Palette =>
	!!value &&
	typeof value === 'object' &&
	SHADES.every(shade => {
		const shadeValue = (value as Palette)[shade]
		return typeof shadeValue === 'string' && COLOR_VALUE.test(shadeValue)
	})

/** `primary` → `--chakra-colors-primary-500`, as Chakra names them. */
const cssVar = (name: string, shade?: number) =>
	`--chakra-colors-${name}${shade === undefined ? '' : `-${shade}`}`

const knob = (name: string | undefined, fallback: string) =>
	name ? `var(${name}, ${fallback})` : fallback

/**
 * The dark block for a Chakra theme's `colors`. Only full 50–900 palettes are
 * mirrored; alpha ramps (whiteAlpha, blackAlpha) are left alone, which is what
 * keeps content on a FIXED colour — an overlay, a payment brand chip — white.
 */
export function formDarkPaletteCss(colors: Record<string, unknown>): string {
	const decls: string[] = []

	for (const [name, value] of Object.entries(colors)) {
		if (!/^[a-z][\w-]*$/i.test(name) || /Alpha$/.test(name)) continue
		if (!isPalette(value)) continue
		SHADES.forEach((shade, index) => {
			// By position, not arithmetic: 50 pairs with 900, 400 with 500.
			const resolved = String(value[SHADES[SHADES.length - 1 - index]])
			decls.push(
				`${cssVar(name, shade)}: ${knob(name === 'gray' ? GRAY_KNOBS[shade] : undefined, resolved)};`
			)
		})
	}

	const gray = colors.gray
	if (isPalette(gray)) {
		decls.push(
			`${cssVar('white')}: ${knob('--crosure-dark-surface-raised', String(gray[800]))};`,
			`${cssVar('black')}: ${knob('--crosure-dark-fg', String(gray[50]))};`
		)
	}

	const darkFg = isPalette(gray) ? String(gray[50]) : '#F7FAFC'
	const darkSurface = isPalette(gray) ? String(gray[900]) : '#171923'
	return [
		`html[${COLOR_MODE_ATTRIBUTE}="dark"] {`,
		// !important: Chakra's ColorModeProvider writes color-scheme inline on
		// <html>, and native controls (date pickers, scrollbars) follow it.
		'  color-scheme: dark !important;',
		...decls.map(decl => `  ${decl}`),
		'}',
		// The theme sets <body>'s colours as literal hex (themes/index.ts), and
		// everything that inherits them — text with no colour of its own, and
		// modals or menus portalled straight into <body> — would stay dark ink
		// on a dark page.
		`html[${COLOR_MODE_ATTRIBUTE}="dark"] body {`,
		`  color: var(--crosure-dark-fg, ${darkFg});`,
		`  background-color: var(--crosure-dark-surface, ${darkSurface});`,
		'}',
	].join('\n')
}
